import React, { useState, useEffect } from 'react';
import { MessageSquare, Loader2, Send, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });
const DEFAULT_MSG = "Halo! Ripot kad blong {student} ({term}) i rere nao. Klik: {link}";

const parseYears = (s) => {
  if (!s) return Array.from({ length: 13 }, (_, i) => i + 1);
  if (s.includes('-')) {
    const [a, b] = s.split('-').map(Number);
    return Array.from({ length: b - a + 1 }, (_, i) => a + i);
  }
  return s.split(',').map(Number);
};

export default function SmsNotificationsPage() {
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState('');
  const [year, setYear] = useState('');
  const [term, setTerm] = useState('Term 2');
  const [message, setMessage] = useState(DEFAULT_MSG);
  const [previewData, setPreviewData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);
  const [testPhone, setTestPhone] = useState('');

  useEffect(() => {
    fetch('/api/sms/schools', { headers: authHeaders() }).then((r) => r.json()).then((d) => {
      const list = d.schools || [];
      setSchools(list);
      if (list.length === 1) {
        setSchoolId(list[0].id);
        loadPreview(list[0].id, '');
      }
    });
  }, []);

  const loadPreview = async (sc, yr) => {
    if (!sc) { setPreviewData(null); return; }
    const res = await fetch(`/api/sms/report-notify/preview?school_id=${sc}${yr ? `&year=${yr}` : ''}`, { headers: authHeaders() });
    setPreviewData(await res.json());
  };

  const sendAll = async () => {
    if (!window.confirm(`Send SMS to ${previewData?.count} parents? This uses your Twilio credit.`)) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await fetch('/api/sms/report-notify', {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ school_id: schoolId, year: year ? Number(year) : null, term, message }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail);
      setProgress({ total: d.total, sent: 0, failed: 0, status: 'running' });
      const poll = setInterval(async () => {
        try {
          const sr = await fetch(`/api/sms/report-notify/status/${d.batch_id}`, { headers: authHeaders() });
          const s = await sr.json();
          setProgress(s);
          if (s.status === 'done') {
            clearInterval(poll);
            setBusy(false);
            setResult({ sent: s.sent, total: s.total, failed: s.failed ? [{ error: s.last_error, count: s.failed }] : [] });
            if (s.failed) toast.warning(`Done: ${s.sent} sent, ${s.failed} failed`);
            else toast.success(`All ${s.sent} SMS sent!`);
          }
        } catch (err) { /* keep polling */ }
      }, 2000);
    } catch (e) { toast.error(e.message || 'Send failed'); setBusy(false); }
  };

  const sendTest = async () => {
    if (!testPhone) { toast.error('Enter a phone number'); return; }
    setBusy(true);
    try {
      const res = await fetch('/api/sms/test', {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: testPhone, message: message.replace('{student}', 'Test Student').replace('{parent}', 'Parent') }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail);
      toast.success('Test SMS sent! Check the phone.');
    } catch (e) { toast.error(e.message || 'Test failed'); }
    setBusy(false);
  };

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="sms-page">
      <h1 className="text-2xl font-bold flex items-center gap-2 mb-1"><MessageSquare className="w-7 h-7 text-green-600" /> SMS to Parents</h1>
      <p className="text-gray-500 text-sm mb-6">Notify parents by SMS when report cards are ready</p>

      <div className="bg-white border rounded-xl p-5 space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-medium">School</label>
            <select value={schoolId} onChange={(e) => { setSchoolId(e.target.value); setYear(''); loadPreview(e.target.value, ''); }}
              className="w-full border rounded-md h-10 px-2 text-sm" data-testid="sms-school-select">
              {schools.length !== 1 && <option value="">Select school…</option>}
              {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Year level (optional)</label>
            <select value={year} onChange={(e) => { setYear(e.target.value); loadPreview(schoolId, e.target.value); }}
              className="w-full border rounded-md h-10 px-2 text-sm" data-testid="sms-year-select">
              <option value="">All years</option>
              {parseYears(schools.find((s) => s.id === schoolId)?.years_offered).map((y) => <option key={y} value={y}>Year {y}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Term</label>
            <select value={term} onChange={(e) => setTerm(e.target.value)}
              className="w-full border rounded-md h-10 px-2 text-sm" data-testid="sms-term-select">
              {['Term 1', 'Term 2', 'Term 3'].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="text-sm font-medium">Message — {'{student}'}, {'{parent}'}, {'{term}'} = auto-filled, {'{link}'} = report link</label>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
            className="w-full border rounded-md p-2 text-sm" data-testid="sms-message-input" />
          <div className="text-xs text-gray-400">{message.length} characters {message.length > 160 && '(over 160 = billed as 2+ SMS each)'}</div>
        </div>
        {previewData && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800" data-testid="sms-preview-count">
            📱 <b>{previewData.count} parents</b> will receive this SMS
          </div>
        )}
        <Button onClick={sendAll} disabled={busy || !previewData?.count} className="w-full bg-green-600 hover:bg-green-700" data-testid="sms-send-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Send to {previewData?.count || 0} Parents
        </Button>
        {progress && progress.status === 'running' && (
          <div className="space-y-1" data-testid="sms-progress">
            <div className="flex justify-between text-xs text-gray-600">
              <span>Sending… {progress.sent + progress.failed} / {progress.total}</span>
              {progress.failed > 0 && <span className="text-red-600">{progress.failed} failed</span>}
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div className="bg-green-600 h-2.5 rounded-full transition-all"
                style={{ width: `${Math.round(((progress.sent + progress.failed) / (progress.total || 1)) * 100)}%` }} />
            </div>
            <p className="text-xs text-gray-400">You can leave this page — sending continues on the server.</p>
          </div>
        )}
        {result && (
          <div className="text-sm space-y-2" data-testid="sms-result">
            <div className="flex items-center gap-2 text-green-700"><CheckCircle2 className="w-4 h-4" /> Sent: {result.sent} / {result.total}</div>
            {result.failed.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                <div className="flex items-center gap-1 text-red-700 font-medium mb-1"><AlertTriangle className="w-4 h-4" /> Failed: {result.failed[0].count}</div>
                <div className="text-xs text-red-600">Last error: {result.failed[0].error}</div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border rounded-xl p-5 mt-6">
        <h3 className="font-semibold mb-2">🧪 Send a test SMS first</h3>
        <div className="flex gap-2">
          <Input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="Your phone e.g. 5551234" data-testid="sms-test-phone" />
          <Button variant="outline" onClick={sendTest} disabled={busy} data-testid="sms-test-btn">Send Test</Button>
        </div>
        <p className="text-xs text-gray-400 mt-2">Twilio trial can only send to numbers you verified in the Twilio console.</p>
      </div>
    </div>
  );
}
